import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Target, CalendarDays, Award, LogOut, Clock, MapPin, RotateCcw, X, CheckCircle2, ArrowUpRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { COURSE_IMAGES } from "../data/media";
import BookingDialog from "../components/portal/BookingDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Calendar } from "../components/ui/calendar";

const STATUS_STYLES = {
  pending_payment: "text-brand-brass border-brand-brass/40",
  confirmed: "text-brand-orange border-brand-orange/40",
  completed: "text-[#34C759] border-[#34C759]/40",
  cancelled: "text-brand-muted border-white/15 line-through",
};
const STATUS_LABEL = {
  pending_payment: "Payment pending",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
};

function StatCard({ icon: Icon, value, label, testid }) {
  return (
    <div className="border border-white/10 bg-brand-surface p-6 rounded-sm" data-testid={testid}>
      <Icon size={20} className="text-brand-orange" />
      <div className="mt-6 font-heading font-black text-4xl text-brand-ink">{value}</div>
      <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-brand-muted">{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 rounded-sm ${STATUS_STYLES[status] || ""}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("overview");
  const [adminBookings, setAdminBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [adminDate, setAdminDate] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const [c, b] = await Promise.all([api.get("/courses"), api.get("/bookings")]);
      setCourses(c.data.courses);
      setSlots(c.data.time_slots);
      setBookings(b.data);
    } catch (e) {}
  }, []);

  const loadAdmin = useCallback(async () => {
    if (!user?.is_admin) return;
    try {
      const [ab, bl] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/blocks")]);
      setAdminBookings(ab.data);
      setBlocks(bl.data);
    } catch (e) {}
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "admin") loadAdmin(); }, [tab, loadAdmin]);

  const toggleBlock = async (dateObj, s) => {
    const iso = format(dateObj, "yyyy-MM-dd");
    const isBlocked = blocks.some((b) => b.date === iso && b.time_slot === s);
    try {
      await api.post(isBlocked ? "/admin/unblock" : "/admin/block", { date: iso, time_slot: s });
      toast.success(isBlocked ? "Slot opened." : "Slot blocked.");
      loadAdmin();
    } catch (e) { toast.error("Could not update slot."); }
  };

  const upcoming = bookings.filter((b) => b.display_status === "confirmed");
  const completed = bookings.filter((b) => b.display_status === "completed");
  const certs = completed.filter((b) => b.certificate_id);
  const nextSession = [...upcoming].sort((a, b) => new Date(a.date) - new Date(b.date))[0];

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled.");
      load();
    } catch (e) { toast.error("Could not cancel."); }
  };

  const fmt = (d) => { try { return format(new Date(d), "EEE, MMM d yyyy"); } catch { return d; } };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-brand-bg/80 backdrop-blur-2xl border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="portal-logo" className="flex items-center gap-3 group">
            <img src="/logo-mark.png" alt="Midwest Made" className="h-9 w-9 group-hover:scale-110 transition-transform duration-500" />
            <span className="font-heading font-black text-lg tracking-tight uppercase leading-none">
              Midwest<span className="text-brand-orange">Made</span>
            </span>
          </button>
          <div className="flex items-center gap-5">
            <div className="hidden sm:flex flex-col items-end">
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-brand-muted">Member</span>
              <span className="text-sm text-brand-ink">{user?.name}</span>
            </div>
            <Avatar className="h-9 w-9 rounded-sm border border-white/10">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="rounded-sm bg-brand-surface text-brand-orange font-mono text-xs">
                {user?.name?.[0] || "M"}
              </AvatarFallback>
            </Avatar>
            <button onClick={logout} data-testid="logout-btn" className="text-brand-muted hover:text-brand-orange transition-colors">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-orange">Member Portal</span>
          <h1 className="mt-4 font-heading font-black uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9]">
            Welcome back,<br /><span className="text-brand-brass">{user?.name?.split(" ")[0] || "shooter"}.</span>
          </h1>
        </motion.div>

        <Tabs value={tab} onValueChange={setTab} className="mt-12">
          <TabsList className="bg-brand-surface border border-white/10 rounded-sm p-1 h-auto flex-wrap">
            {[
              { v: "overview", l: "Overview" },
              { v: "book", l: "Book a Session" },
              { v: "bookings", l: "My Bookings" },
              { v: "progress", l: "Progress" },
              ...(user?.is_admin ? [{ v: "admin", l: "Admin" }] : []),
            ].map((t) => (
              <TabsTrigger
                key={t.v} value={t.v} data-testid={`tab-${t.v}`}
                className="rounded-sm font-mono text-[11px] uppercase tracking-[0.15em] data-[state=active]:bg-brand-orange data-[state=active]:text-brand-bg px-4 py-2.5"
              >
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={CalendarDays} value={upcoming.length} label="Upcoming sessions" testid="stat-upcoming" />
              <StatCard icon={CheckCircle2} value={completed.length} label="Completed" testid="stat-completed" />
              <StatCard icon={Award} value={certs.length} label="Certifications" testid="stat-certs" />
              <StatCard icon={Target} value={bookings.length} label="Total bookings" testid="stat-total" />
            </div>

            <div className="mt-6 border border-white/10 bg-brand-surface rounded-sm p-8">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-brass">Next up</span>
              {nextSession ? (
                <div className="mt-5 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="next-session">
                  <div>
                    <h3 className="font-heading font-bold uppercase text-2xl md:text-3xl tracking-tight">{nextSession.course_name}</h3>
                    <div className="mt-3 flex flex-wrap items-center gap-5 text-brand-muted text-sm">
                      <span className="flex items-center gap-2"><CalendarDays size={15} className="text-brand-orange" />{fmt(nextSession.date)}</span>
                      <span className="flex items-center gap-2"><Clock size={15} className="text-brand-orange" />{nextSession.time_slot}</span>
                      <span className="flex items-center gap-2"><MapPin size={15} className="text-brand-orange" />St. Louis Range</span>
                    </div>
                  </div>
                  <button onClick={() => setTab("bookings")} data-testid="manage-next-btn" className="font-mono text-xs uppercase tracking-[0.2em] border border-white/20 px-6 py-3 hover:border-brand-orange transition-colors">
                    Manage
                  </button>
                </div>
              ) : (
                <div className="mt-5">
                  <p className="text-brand-muted text-sm">No confirmed sessions yet. Book your first one to get on the range.</p>
                  <button onClick={() => setTab("book")} data-testid="overview-book-btn" className="mt-5 group flex items-center gap-2 bg-brand-orange text-brand-bg font-mono text-xs uppercase tracking-[0.2em] px-6 py-3.5 hover:bg-brand-ink transition-colors">
                    Book a session <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* BOOK */}
          <TabsContent value="book" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {courses.map((c) => (
                <div key={c.id} className="group border border-white/10 bg-brand-surface rounded-sm overflow-hidden" data-testid={`portal-course-${c.id}`}>
                  <div className="relative h-44 overflow-hidden">
                    <img src={COURSE_IMAGES[c.id]} alt={c.name} className={`w-full h-full object-cover img-treat ${c.id === "pistol-fundamentals" ? "object-top" : "object-center"}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-surface to-transparent" />
                    <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.15em] bg-brand-bg/70 backdrop-blur px-3 py-1.5 text-brand-brass border border-white/10">{c.level}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-heading font-bold uppercase text-xl tracking-tight">{c.name}</h3>
                      <span className="font-heading font-black text-2xl">${c.price.toFixed(0)}</span>
                    </div>
                    <p className="mt-2 text-brand-muted text-sm leading-relaxed">{c.tagline}</p>
                    <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-5">
                      <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-brand-muted flex items-center gap-2"><Clock size={13} />{c.duration}</span>
                      <BookingDialog
                        course={c} timeSlots={slots} onBooked={load}
                        trigger={
                          <button data-testid={`portal-book-${c.id}`} className="group/b flex items-center gap-2 bg-brand-orange text-brand-bg font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-brand-ink transition-colors">
                            Book <ArrowUpRight size={14} className="group-hover/b:translate-x-0.5 group-hover/b:-translate-y-0.5 transition-transform" />
                          </button>
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-8">
            {bookings.length === 0 ? (
              <div className="border border-white/10 bg-brand-surface rounded-sm p-12 text-center">
                <p className="text-brand-muted text-sm">No bookings yet.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="bookings-list">
                {bookings.map((b) => (
                  <div key={b.id} className="border border-white/10 bg-brand-surface rounded-sm p-6 flex flex-col md:flex-row md:items-center justify-between gap-5" data-testid={`booking-row-${b.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <h3 className="font-heading font-bold uppercase text-xl tracking-tight">{b.course_name}</h3>
                        <StatusBadge status={b.display_status} />
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-5 text-brand-muted text-sm">
                        <span className="flex items-center gap-2"><CalendarDays size={14} className="text-brand-orange" />{fmt(b.date)}</span>
                        <span className="flex items-center gap-2"><Clock size={14} className="text-brand-orange" />{b.time_slot}</span>
                        <span className="font-mono text-brand-ink">${Number(b.amount).toFixed(0)}</span>
                      </div>
                    </div>

                    {(b.display_status === "confirmed" || b.display_status === "pending_payment") && (
                      <div className="flex items-center gap-3">
                        {b.display_status === "confirmed" && (
                          <BookingDialog
                            timeSlots={slots} onBooked={load} initialBooking={b}
                            trigger={
                              <button data-testid={`reschedule-${b.id}`} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] border border-white/20 px-4 py-2.5 hover:border-brand-orange transition-colors">
                                <RotateCcw size={13} /> Reschedule
                              </button>
                            }
                          />
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`cancel-${b.id}`} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] border border-white/20 text-brand-muted px-4 py-2.5 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors">
                              <X size={13} /> Cancel
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-brand-surface border border-white/10 rounded-sm text-brand-ink">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-heading uppercase tracking-tight">Cancel this booking?</AlertDialogTitle>
                              <AlertDialogDescription className="text-brand-muted">
                                This will cancel your {b.course_name} session on {fmt(b.date)}. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-sm bg-transparent border-white/20 text-brand-ink hover:bg-white/5">Keep it</AlertDialogCancel>
                              <AlertDialogAction data-testid={`confirm-cancel-${b.id}`} onClick={() => cancel(b.id)} className="rounded-sm bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80">Cancel booking</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PROGRESS */}
          <TabsContent value="progress" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 border border-white/10 bg-brand-surface rounded-sm p-8">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-brass">Completed training</span>
                {completed.length === 0 ? (
                  <p className="mt-5 text-brand-muted text-sm">Your completed sessions will appear here after you train.</p>
                ) : (
                  <div className="mt-6 space-y-3" data-testid="completed-list">
                    {completed.map((b) => (
                      <div key={b.id} className="flex items-center justify-between border-b border-white/10 pb-3">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 size={18} className="text-[#34C759]" />
                          <div>
                            <div className="font-heading font-bold uppercase tracking-tight">{b.course_name}</div>
                            <div className="font-mono text-[11px] text-brand-muted">{fmt(b.date)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-white/10 bg-brand-surface rounded-sm p-8">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-brass">Certifications</span>
                {certs.length === 0 ? (
                  <p className="mt-5 text-brand-muted text-sm">Earn certifications by completing courses.</p>
                ) : (
                  <div className="mt-6 space-y-4" data-testid="certs-list">
                    {certs.map((b) => (
                      <div key={b.id} className="border border-brand-brass/30 rounded-sm p-4 bg-brand-brass/5">
                        <Award size={18} className="text-brand-brass" />
                        <div className="mt-3 font-heading font-bold uppercase text-sm tracking-tight">{b.course_name}</div>
                        <div className="font-mono text-[10px] text-brand-muted mt-1">CERT · {b.certificate_id}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ADMIN */}
          {user?.is_admin && (
            <TabsContent value="admin" className="mt-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-white/10 bg-brand-surface rounded-sm p-6">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-brass">All bookings</span>
                    <span className="font-mono text-[11px] text-brand-muted">{adminBookings.length} total</span>
                  </div>
                  {adminBookings.length === 0 ? (
                    <p className="mt-5 text-brand-muted text-sm">No bookings across the range yet.</p>
                  ) : (
                    <div className="mt-5 space-y-2" data-testid="admin-bookings-list">
                      {adminBookings.map((b) => (
                        <div key={b.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-3" data-testid={`admin-booking-${b.id}`}>
                          <div>
                            <div className="flex items-center gap-3">
                              <span className="font-heading font-bold uppercase tracking-tight">{b.course_name}</span>
                              <StatusBadge status={b.display_status} />
                            </div>
                            <div className="font-mono text-[11px] text-brand-muted mt-1">{b.member_name} · {b.member_email}</div>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-brand-muted">
                            <span className="flex items-center gap-1.5"><CalendarDays size={13} className="text-brand-orange" />{fmt(b.date)}</span>
                            <span className="flex items-center gap-1.5"><Clock size={13} className="text-brand-orange" />{b.time_slot}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border border-white/10 bg-brand-surface rounded-sm p-6">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-brass">Block time slots</span>
                  <p className="mt-2 text-brand-muted text-xs leading-relaxed">Pick a date, then toggle slots to open or block them for bookings.</p>
                  <div className="mt-4 flex justify-center border border-white/10 rounded-sm p-2">
                    <Calendar
                      mode="single"
                      selected={adminDate}
                      onSelect={(d) => d && setAdminDate(d)}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      data-testid="admin-calendar"
                    />
                  </div>
                  <div className="mt-4 space-y-2">
                    {slots.map((s) => {
                      const iso = format(adminDate, "yyyy-MM-dd");
                      const isBlocked = blocks.some((b) => b.date === iso && b.time_slot === s);
                      return (
                        <button
                          key={s}
                          onClick={() => toggleBlock(adminDate, s)}
                          data-testid={`admin-slot-${s}`}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-sm border font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                            isBlocked
                              ? "border-[#FF3B30]/40 text-[#FF3B30] bg-[#FF3B30]/5"
                              : "border-white/10 text-brand-ink hover:border-brand-orange"
                          }`}
                        >
                          <span>{s}</span>
                          <span>{isBlocked ? "Blocked · open it" : "Open · block it"}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
